import RenderComponentOrLoading from '@Components/RenderComponentOrLoading';
import {
	InfoCard,
	InputAutocomplete,
	InputAutocompleteMulti,
	InputText,
	SensorGroupAutocomplete,
	SensorsAutocomplete,
	useSelectImages,
	useSelectPaginatedSensorGroups,
	useSelectSensors,
} from '@innovyze/stylovyze';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import { LiftStationFormFields, STATUS_ENUM, VirtualSensor } from '@Types';
import { FACILITIES_TYPES_LIST } from '@innovyze/shared-utils';
import { useFormikContext } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';
import useGetLocationsFromSensorsInFormik from './hooks/useGetLocationsFromSensorsInFormik';
interface Option {
	value: string;
	key: string;
}
export default function UpsertLiftStationFormFields() {
	const { t } = useGlobalization();
	const { touched, errors, setFieldTouched } =
		useFormikContext<LiftStationFormFields>();
	useGetLocationsFromSensorsInFormik();

	const { initialized: sensorsInitialized } = useSelectSensors();

	const { status: volumeSensorsStatus, sensors: volumeSensors } =
		useSelector(getValue).sensors.virtualSensors;

	const { images } = useSelectImages();
	const imagesOptions = images.map(image => ({
		key: image._id,
		value: image.name,
	}));

	const volumeSensorsInitialized =
		volumeSensorsStatus === STATUS_ENUM.SUCCESS;

	const initialized = sensorsInitialized;
	const { status } = useSelectPaginatedSensorGroups();

	const volumeSensorsOptions: Option[] = volumeSensors
		.map((sensor: VirtualSensor) => ({
			key: sensor.sensorId,
			value: sensor.sensorAlias || sensor.sensorId,
		}))
		.filter(({ key, value }) => key && value);

	const facilitiesOptions = FACILITIES_TYPES_LIST.map(type => ({
		key: type,
		value: type,
	}));

	return (
		<InfoCard fullHeight title={t('Facility Definition')}>
			<RenderComponentOrLoading
				loading={!initialized || status === STATUS_ENUM.IDLE}>
				<InputText
					dataCy="stationNameField"
					textField={{
						label: t('Name'),
						error: touched.name && !!errors?.name,
						helperText: touched.name && errors?.name,
						variant: 'outlined',
						required: true,
					}}
					fieldAttrs={{ name: 'name' }}
				/>
				<InputAutocomplete
					dataCy="stationTypeField"
					label={t('Type')}
					options={facilitiesOptions}
					fieldAttrs={{ name: 'type' }}
					textFieldProps={{
						required: true,
						onBlur: () => {
							setFieldTouched('type', true);
						},
					}}
					autocompleteProps={{
						disableClearable: true,
						noOptionsText: t('No Options'),
					}}
				/>
				<InputText
					dataCy="locationField"
					textField={{
						label: t('Location'),
						error: touched.location && !!errors?.location,
						helperText: touched.location && errors?.location,
					}}
					fieldAttrs={{ name: 'location' }}
				/>
				<SensorGroupAutocomplete
					dataCy="sensorGroupsField"
					label={t('Sensor groups')}
					fieldAttrs={{ name: 'sensorGroups' }}
					helperText={
						touched.sensorGroups ? errors.sensorGroups : undefined
					}
					error={
						touched.sensorGroups ? !!errors.sensorGroups : undefined
					}
					textFieldProps={{ required: true }}
					autocompleteProps={{
						disableClearable: true,
						disabled: status !== STATUS_ENUM.SUCCESS,
						noOptionsText: t('No Options'),
					}}
				/>
				<SensorsAutocomplete
					dataCy="sensorsField"
					label={t('Data Sources')}
					autocompleteProps={{
						disableClearable: true,
						disabled: !sensorsInitialized,
						noOptionsText: t('No Options'),
					}}
					fieldAttrs={{ name: 'sensors' }}
					helperText={touched.sensors ? errors.sensors : undefined}
					error={touched.sensors ? !!errors.sensors : undefined}
					textFieldProps={{ required: true }}
				/>
				<InputAutocomplete
					dataCy="calculatedVolumeSensorField"
					label={t('Calculated Volume')}
					options={volumeSensorsOptions}
					fieldAttrs={{ name: 'calculatedVolumeSensor' }}
					textFieldProps={{
						required: false,
						onBlur: () => {
							setFieldTouched('calculatedVolumeSensor', true);
						},
					}}
					autocompleteProps={{
						disabled: !volumeSensorsInitialized,
						noOptionsText: t('No Options'),
					}}
				/>
				<InputAutocompleteMulti
					dataCy="imagesField"
					options={imagesOptions}
					fieldAttrs={{ name: 'images' }}
					label={t('Images')}
					autocompleteProps={{
						disableClearable: true,
						noOptionsText: t('No Options'),
					}}
				/>
			</RenderComponentOrLoading>
		</InfoCard>
	);
}
